import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { getArticle } from "../data/journal";
import { ARTICLES } from "../data/journal";
import PogLogo from "../components/PogLogo";
import ShareBar from "../components/ShareBar";

const CANONICAL = "https://pogspirits.com";

const Article = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) return <Navigate to="/journal" replace />;

  const url = `${CANONICAL}/journal/${article.slug}`;
  const related = (article.related || [])
    .map((s) => ARTICLES.find((a) => a.slug === s))
    .filter(Boolean);

  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        headline: article.title,
        description: article.description,
        datePublished: article.date,
        dateModified: article.date,
        image: article.hero.startsWith("http") ? article.hero : `${CANONICAL}${article.hero}`,
        url: url,
        inLanguage: "en-US",
        author: { "@type": "Organization", name: "POG Spirits", url: CANONICAL },
        publisher: {
          "@type": "Organization",
          name: "POG Spirits",
          logo: { "@type": "ImageObject", url: `${CANONICAL}/brand/pog-logo.png` },
        },
        mainEntityOfPage: url,
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "[data-speakable]"],
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: CANONICAL },
          { "@type": "ListItem", position: 2, name: "Journal", item: `${CANONICAL}/journal` },
          { "@type": "ListItem", position: 3, name: article.title, item: url },
        ],
      },
      ...(article.faq && article.faq.length
        ? [
            {
              "@type": "FAQPage",
              mainEntity: article.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <div className="min-h-screen bg-[#14100D] text-[#EFE7D6]">
      <Helmet>
        <title>{article.metaTitle || article.title}</title>
        <meta name="description" content={article.description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.description} />
        <meta property="og:url" content={url} />
        <meta
          property="og:image"
          content={article.hero.startsWith("http") ? article.hero : `${CANONICAL}${article.hero}`}
        />
        <meta property="article:published_time" content={article.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
      </Helmet>

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#14100D]/90 backdrop-blur-xl border-b border-[#EFE7D6]/10">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <PogLogo size={28} />
            <span className="font-body italic text-[#EFE7D6] tracking-wide">Spirits</span>
          </Link>
          <Link
            to="/journal"
            className="font-body text-[11px] tracking-[0.28em] uppercase text-[#EFE7D6]/75 hover:text-[#E85A15] transition-colors link-underline"
            data-testid="article-back-journal"
          >
            ← The Journal
          </Link>
        </div>
      </header>

      <main className="pt-36 pb-32">
        <article className="max-w-3xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="overline">{article.category}</span>
              <span className="w-10 hairline-strong h-px inline-block" />
              <span className="counter">{article.readMinutes} min read</span>
            </div>
            <h1 className="display-tight text-4xl md:text-6xl mb-10" data-testid="article-title">
              {article.title}
            </h1>

            {/* Answer-first block for AEO / voice */}
            <div
              className="border-l-2 border-[#E85A15] pl-6 mb-12"
              data-speakable
              data-testid="article-answer"
            >
              <p className="font-body text-xl md:text-2xl text-[#EFE7D6] leading-[1.5] font-light">
                {article.answer}
              </p>
            </div>
          </motion.div>

          <div
            className="relative aspect-[16/9] overflow-hidden mb-12"
            style={{ background: "radial-gradient(ellipse at 50% 45%, #2a1f18 0%, #0f0a07 80%)" }}
          >
            <img
              src={article.hero}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          <div className="space-y-10">
            {article.body.map((section) => (
              <section key={section.h}>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-4 text-[#EFE7D6]">
                  {section.h}
                </h2>
                <p className="font-body text-[#DCCFB4]/85 text-lg leading-[1.8] font-light">
                  {section.p}
                </p>
              </section>
            ))}
          </div>

          {/* Article FAQ */}
          {article.faq && article.faq.length > 0 && (
            <div className="mt-16 pt-12 border-t border-[#EFE7D6]/12">
              <h2 className="font-display text-2xl md:text-3xl mb-8">Common questions</h2>
              <div className="space-y-6">
                {article.faq.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-display text-lg text-[#EFE7D6] mb-2">{f.q}</h3>
                    <p className="font-body text-[#DCCFB4]/80 leading-relaxed font-light">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-14 pt-10 border-t border-[#EFE7D6]/12 flex flex-wrap items-center justify-between gap-6">
            <ShareBar url={url} title={article.title} testid="article-share" />
            <Link to="/#inquire" className="btn-primary" data-testid="article-cta">
              Carry POG
            </Link>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-8 md:px-16 mt-28">
            <div className="overline mb-8">Keep reading</div>
            <div className="grid md:grid-cols-2 gap-10">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/journal/${r.slug}`}
                  className="group flex gap-6 items-center border-t border-[#EFE7D6]/12 pt-6"
                  data-testid={`related-${r.slug}`}
                >
                  <div className="flex-1">
                    <div className="overline text-[#DCCFB4]/50 mb-2 text-[10px]">{r.category}</div>
                    <h3 className="font-display text-xl md:text-2xl tracking-tight group-hover:text-[#E85A15] transition-colors">
                      {r.title}
                    </h3>
                  </div>
                  <span className="counter text-[#E85A15]">→</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Article;
